/* Coverage-confidence derivation (C1, roadmap P0 #1 / AM-persona P0 #1).
 *
 * Coverage discipline is the tool's stated #1 mandate ("silent under-coverage
 * is the primary failure mode"), yet the only per-phase accounting lived in a
 * bottom-of-appendix accordion whose own intro disclaimed itself — its figures
 * "may lag the final reconciled counts used everywhere else on the page." This
 * builds the RECONCILED per-layer view that resolves that disclaimer at the
 * source, so the exec summary can promote a trustworthy coverage panel.
 *
 * THE RECONCILIATION: the mapping agent records per-phase search/fetch counts
 * in `state.coverage` (a Record<phaseKey, prose>). Two phases — search-presence
 * (phase 5) and AI-narrative (phase 6) — are written as "deferred to cross-
 * referencing stage" because their real work happens downstream in the IMPACT
 * and AIQ enrichment planes. Those planes (`state.impact`, `state.aiNarrative`)
 * are already in the payload and are what the Search/AI sections elsewhere on
 * the page render. So here we OVERRIDE the two deferred notes with the measured
 * enrichment presence — the same "joined, measured data" used everywhere else —
 * instead of reusing the lagging mapping notes. No backend touch is required:
 * everything needed is already on the state.
 *
 * SCOPED LAYERS (Design C1 blocker): a profile-scoped run deliberately skips
 * some of the four tailorable layers (owned/claimable/community/media). Those
 * are recorded in `state.skippedLayers` and already announced by the §0
 * `.wsscope` provenance line ("claimable layer skipped — deliberately not
 * checked, not missing"). A skipped layer must therefore render as a neutral
 * `scoped` cell — NOT an amber "not run" gap — and must be excluded from the
 * thin count and the in-scope denominator, so the panel and the scope line
 * never tell opposite stories about the same layer. The layer→phase map below
 * shares SCOPE_LAYER_LABEL's keys so the two surfaces speak identically.
 *
 * Pure and unit-tested (derive/coverage.test.ts) against real El Al fixtures.
 */
import type { AppPayloadState } from './state-shape.ts';
import { SCOPE_LAYER_LABEL } from './scope.ts';

// The four tailorable scope layers (SCOPE_LAYER_LABEL keys) → the coverage
// phase number each maps to (phase1_owned_editable, phase2_claimable,
// phase3_community_reference, phase4_media_risk). Phases 5–9 are never
// profile-scopable, so they never read as "scoped".
const SCOPE_LAYER_PHASE: Record<string, number> = {
  owned: 1,
  claimable: 2,
  community: 3,
  media: 4,
};

export interface CoverageLayer {
  /** phase number 1..9 (from the `phaseN_*` coverage key). */
  n: number;
  key: string;
  /** human label derived from the run's own phase key. */
  label: string;
  searches: number | null;
  fetches: number | null;
  /** where the coverage for this layer actually came from. */
  source: 'mapping' | 'IMPACT' | 'AIQ';
  /** for enrichment-sourced layers: what was measured (e.g. "6 SERP queries"). */
  measuredNote: string | null;
  /** was the layer actually covered (by mapping, or reconciled via enrichment)? */
  checked: boolean;
  /** flagged thin — either a "THIN:" sub-caveat or an uncovered gap. Always
   * false for a scoped layer (deliberately skipped ≠ thin). */
  thin: boolean;
  /** the "THIN: …" detail from the mapping note, if any. */
  thinNote: string | null;
  /** deliberately skipped by the client profile (state.skippedLayers). Renders
   * as a neutral ghost cell, excluded from thinCount and the in-scope total. */
  scoped: boolean;
}

export interface CoveragePanel {
  layers: CoverageLayer[];
  total: number;
  /** layers not scoped out — the honest denominator for "checked". */
  inScopeTotal: number;
  /** checked layers, among in-scope only. */
  checkedCount: number;
  /** thin layers, among in-scope only (scoped layers never count as thin). */
  thinCount: number;
  /** layers deliberately skipped by the client profile. */
  scopedCount: number;
  allChecked: boolean;
  /** every in-scope layer checked and none thin — the only fully-green state. */
  clean: boolean;
}

// A mapping note that starts with one of these is a deferral / not-really-run
// marker (as opposed to a substantive completion note like phase 7's
// "Completed within phase 4 + synthesis: …").
const DEFERRED = /^\s*(deferred|light in stage|not run|no dedicated|skipped|n\/a|none\b)/i;

function parseSearches(s: string): number | null {
  // "16 searches", "17+ API calls/searches"
  const m = /(\d+)\s*\+?\s*(?:API calls\/)?searches/i.exec(s);
  return m ? parseInt(m[1] ?? '', 10) : null; // group 1 always captures
}
function parseFetches(s: string): number | null {
  // "~20 fetches", "20 fetches (14 ok, 6 blocked)"
  const m = /~?\s*(\d+)\s*\+?\s*fetches/i.exec(s);
  return m ? parseInt(m[1] ?? '', 10) : null; // group 1 always captures
}

/** Human label from the run's phase key, preserving IMPACT/AIQ casing. */
function labelFor(key: string): string {
  const body = key.replace(/^phase\d+_?/, '').replace(/_/g, ' ').trim();
  return body
    .split(/\s+/)
    .map((w) => (/^(impact|aiq|ai|kp)$/i.test(w) ? w.toUpperCase() : w))
    .join(' ')
    .replace(/^./, (c) => c.toUpperCase());
}

/** Reconciled coverage panel, or null if the run carries no phase coverage. */
export function coveragePanel(state: AppPayloadState): CoveragePanel | null {
  const entries = Object.entries(state.coverage || {})
    .map(([key, v]) => ({ key, v: String(v ?? ''), m: /^phase(\d+)/.exec(key) }))
    .filter((e) => e.m)
    .sort((a, b) => Number(a.m![1]) - Number(b.m![1]));
  if (!entries.length) return null;

  // Phases a profile deliberately scoped out. `k in SCOPE_LAYER_LABEL` keeps
  // the two surfaces (this panel + the §0 .wsscope line) keyed off the exact
  // same layer vocabulary, so they can never disagree about a skipped layer.
  const scopedPhases = new Set(
    (state.skippedLayers || [])
      .filter((k) => typeof k === 'string' && k in SCOPE_LAYER_LABEL)
      .map((k) => SCOPE_LAYER_PHASE[k])
      .filter((p): p is number => typeof p === 'number'),
  );

  const layers: CoverageLayer[] = entries.map(({ key, v, m }) => {
    const n = Number(m![1]);
    const scoped = scopedPhases.has(n);
    const searches = parseSearches(v);
    const fetches = parseFetches(v);
    const thinMatch = /THIN:\s*(.*)$/is.exec(v);
    let thinNote: string | null = thinMatch ? (thinMatch[1] ?? '').trim() : null;
    const deferred = DEFERRED.test(v);

    let source: CoverageLayer['source'] = 'mapping';
    let measuredNote: string | null = null;
    // covered by mapping if it reported counts, or if it wrote a substantive
    // (non-deferral) note like "Completed within phase 4".
    let checked = searches != null || fetches != null || (!deferred && v.trim().length > 0);
    let thin = !!thinNote;

    // HONESTY (coverage-honesty audit P2-2): plane-PRESENCE ≠ measurement. The
    // reconciliation only fires when the enrichment plane actually carries
    // measured content (IMPACT has tracked terms; AIQ has engines). A present-
    // but-EMPTY plane object ({}, or {terms:[]}/{engines:[]}) is NOT coverage:
    // it must never fabricate a "measured" note, force `checked` green, or
    // clear a THIN flag the mapping agent legitimately set upstream. An empty
    // plane falls through to the same honest gap/thin logic as a missing plane.
    const impactMeasured = n === 5 && state.impact && (state.impact.terms || []).length > 0;
    const aiqMeasured = n === 6 && state.aiNarrative && (state.aiNarrative.engines || []).length > 0;
    if (impactMeasured) {
      // reconcile search-presence with the IMPACT plane the §1 SERP view uses
      source = 'IMPACT';
      const terms = state.impact!.terms || [];
      measuredNote = `${terms.length} SERP ${terms.length === 1 ? 'query' : 'queries'} tracked`;
      checked = true;
      thin = false;
      thinNote = null;
    } else if (aiqMeasured) {
      // reconcile AI-narrative with the AIQ plane the §3 AI view uses
      source = 'AIQ';
      const engines = state.aiNarrative!.engines || [];
      measuredNote = `${engines.length} AI ${engines.length === 1 ? 'engine' : 'engines'}`;
      checked = true;
      thin = false;
      thinNote = null;
    } else if (!checked) {
      // an uncovered layer with no downstream backfill (missing OR empty plane)
      // is a real coverage gap — and any upstream THIN flag stays intact.
      thin = true;
    }

    // A scoped layer is deliberately skipped, not missing: neutral, never thin.
    // (Provenance wins over any stray count/deferral note the mapping emitted.)
    if (scoped) {
      thin = false;
      thinNote = null;
    }

    return { n, key, label: labelFor(key), searches, fetches, source, measuredNote, checked, thin, thinNote, scoped };
  });

  const inScope = layers.filter((l) => !l.scoped);
  const scopedCount = layers.length - inScope.length;
  const checkedCount = inScope.filter((l) => l.checked).length;
  const thinCount = inScope.filter((l) => l.thin).length;
  return {
    layers,
    total: layers.length,
    inScopeTotal: inScope.length,
    checkedCount,
    thinCount,
    scopedCount,
    allChecked: checkedCount === inScope.length,
    clean: checkedCount === inScope.length && thinCount === 0,
  };
}
